import React, { useCallback, useMemo, useState } from 'react';
import {
  Grid,
  Typography,
  IconButton,
  withStyles,
  Theme,
  Tooltip,
  Hidden,
} from '@material-ui/core';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import { WkblScriptListProps } from 'components/ScriptListView/type';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/reducers/type';
import CodeVO from 'commons/vo/CodeVO';
import CategoryVO from 'commons/vo/CategoryVO';
import StorageVO from 'commons/vo/StorageVO';
import { useStyles } from 'components/ScriptListView/styles';
import clsx from 'clsx';
import TimeCode from 'commons/utils/TimeCode';
import { useVideo } from 'hooks';
import { getDayOfWeek } from 'commons/utils/DateUtils';

const HtmlTooltip = withStyles((theme: Theme) => ({
  tooltip: {
    backgroundColor: '#FFF',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(14),
    border: '1px solid #dadde9',
  },
}))(Tooltip);

function WkblScript({ script, index }: WkblScriptListProps) {
  const classes = useStyles();
  const { wkblSeasonList, wkblGameTypeList } = useSelector((state: RootState) => state.code);
  const { pagination, searchCondition } = useSelector((state: RootState) => state.wkblScript);
  const { storageList } = useSelector((state: RootState) => state.storage);
  const { categoryList } = useSelector((state: RootState) => state.category);

  const [inTimecodeUtil, setInTimecodeUtil] = useState(new TimeCode(script.mediaFps));
  const [outTimecodeUtil, setOutTimecodeUtil] = useState(new TimeCode(script.mediaFps));

  const inTimecode = useMemo(() => {
    inTimecodeUtil.setFrames(script.inPoint);
    return inTimecodeUtil.getTimeCode();
  }, [inTimecodeUtil, script.inPoint]);

  const outTimecode = useMemo(() => {
    outTimecodeUtil.setFrames(script.outPoint);
    return outTimecodeUtil.getTimeCode();
  }, [outTimecodeUtil, script.outPoint]);

  const storage: StorageVO | null = useMemo(() => {
    let result = null;
    const category = categoryList.filter(({ id }: CategoryVO) => id === script.categoryId)[0];
    if (category) {
      result = storageList.filter(({ id }: StorageVO) => id === category.storageId)[0];
    }
    return result;
  }, [categoryList, storageList, script.categoryId]);

  const symbolicPath: string = useMemo(() => {
    if (storage) {
      return storage.symbolicPath;
    }
    return '';
  }, [storage]);

  const seasonName = useMemo(() => {
    const seasonItem: CodeVO[] =
      wkblSeasonList.filter((item: CodeVO) => item.id === Number(script.season)) || [];
    if (seasonItem.length > 0) {
      return seasonItem[0].name || '없음';
    }
    return '없음';
  }, [wkblSeasonList, script.season]);

  const assetNo: number = useMemo(() => {
    const { pageNumber, returnCount } = pagination;
    const retr = returnCount || searchCondition.retr;
    return retr * pageNumber + index + 1;
  }, [pagination, index]);

  const gameTypeName = useMemo(() => {
    const gameTypeItem: CodeVO[] =
      wkblGameTypeList.filter((item: CodeVO) => item.id === Number(script.gameType)) || [];
    if (gameTypeItem.length > 0) {
      return gameTypeItem[0].name || '없음';
    }
    return '없음';
  }, [wkblGameTypeList, script.gameType]);

  const team1LogoPath = useMemo(() => {
    return script.getTeam1LogoPath(symbolicPath);
  }, [script, symbolicPath]);

  const team2LogoPath = useMemo(() => {
    return script.getTeam2LogoPath(symbolicPath);
  }, [script, symbolicPath]);

  const { team1, team2 } = useMemo(() => {
    return script.getTeamLogoPath();
  }, [script]);

  const onPopupAssetDetail = useCallback(() => {
    const inTime = inTimecodeUtil.getSeconds();
    const outTime = outTimecodeUtil.getSeconds();
    // const inTime = Math.round(inTimecodeUtil.getSeconds());
    // const outTime = Math.round(outTimecodeUtil.getSeconds());
    const encodedKeyword = encodeURIComponent(script.script);
    window.open(`/preview/${script.videoId}?in=${inTime}&out=${outTime}&keyword=${encodedKeyword}`);
  }, [script, inTimecodeUtil, outTimecodeUtil]);

  return (
    <div className={clsx(classes.rowContainer, classes.row)} onClick={onPopupAssetDetail}>
      <Hidden mdDown>
        <span className={clsx(classes.headerColumn, classes.seasonContainer)}>{seasonName}</span>
        <span className={clsx(classes.headerColumn, classes.typeContainer)}>{gameTypeName}</span>
        {/* 홈 팀 */}
        <HtmlTooltip
          title={
            <React.Fragment>
              <Typography>{script.team1Name}</Typography>
            </React.Fragment>
          }
        >
          <span className={clsx(classes.headerColumn, classes.teamContainer)}>
            {script.team1 && team1.db && script.team1Name && (
              <div>
                <img src={team1.db}></img>
                <span>{script.team1Name}</span>
              </div>
            )}
          </span>
        </HtmlTooltip>
        {/* 어웨이 팀 */}
        <HtmlTooltip
          title={
            <React.Fragment>
              <Typography>{script.team2Name}</Typography>
            </React.Fragment>
          }
        >
          <span className={clsx(classes.headerColumn, classes.teamContainer)}>
            {script.team2 && team2.db && script.team2Name && (
              <div>
                <img src={team2.db}></img>
                <span>{script.team2Name}</span>
              </div>
            )}
          </span>
        </HtmlTooltip>
        <span
          className={clsx(classes.headerColumn, classes.titleContainer, classes.alignLeftColumn)}
        >
          <div>{script.script}</div>
        </span>
        <span className={clsx(classes.headerColumn, classes.timeContainer)}>{inTimecode}</span>
        <span className={clsx(classes.headerColumn, classes.timeContainer)}>{outTimecode}</span>
        <span className={clsx(classes.headerColumn, classes.gameDateContainer)}>
          {script.formattedGameDate} ({getDayOfWeek(script.formattedGameDate)})
        </span>
      </Hidden>
      <Hidden lgUp>
        <div className={clsx(classes.headerColumn, classes.verticalColumn)}>
          <span>{seasonName}</span>
          <span>
            <div>{gameTypeName}</div>
          </span>
          <span>
            <div className={classes.textEllipsis}>{script.script}</div>
          </span>
          <span>
            <div>
              {inTimecode} - {outTimecode}
            </div>
          </span>
          <span className={clsx(classes.gameDateContainer)}>
            <div>{script.formattedGameDate}</div>
          </span>
        </div>
      </Hidden>
    </div>
  );
}

export default WkblScript;
